import { toast } from "sonner";
import { clearStoredSession, getStoredToken } from "../store/session";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;
export const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const NETWORK_ERROR = "No se pudo conectar con el servidor";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export function toQueryString(q?: Query): string {
  if (!q) return "";
  const p = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { "X-Timezone": TIMEZONE };
  const token = getStoredToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function handleUnauthorized() {
  clearStoredSession();
  if (!window.location.pathname.startsWith("/login")) window.location.assign("/login");
}

async function parseError(res: Response): Promise<ApiError> {
  let detail = "Error inesperado del servidor.";
  try {
    const d = await res.json();
    if (typeof d.detail === "string") detail = d.detail;
    else if (Array.isArray(d.detail)) detail = "Hay datos inválidos en el formulario.";
  } catch {
    // body without JSON
  }
  return new ApiError(res.status, detail);
}

type Opts = { query?: Query; json?: unknown; form?: FormData; raw?: boolean };

export async function request<T>(method: string, path: string, opts: Opts = {}): Promise<T> {
  const headers = authHeaders();
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.form) {
    body = opts.form;
  }
  let res: Response;
  try {
    res = await fetch(API_BASE + path + toQueryString(opts.query), { method, headers, body });
  } catch {
    toast.error(NETWORK_ERROR, { id: "network-error" });
    throw new ApiError(0, NETWORK_ERROR);
  }
  if (res.status === 401 && headers.Authorization) {
    handleUnauthorized();
    throw await parseError(res);
  }
  if (!res.ok) throw await parseError(res);
  if (opts.raw) return res as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>("GET", path, { query }),
  post: <T>(path: string, json?: unknown, query?: Query) => request<T>("POST", path, { json: json ?? {}, query }),
  put: <T>(path: string, json: unknown) => request<T>("PUT", path, { json }),
  del: <T>(path: string, query?: Query) => request<T>("DELETE", path, { query }),
  form: <T>(method: string, path: string, form: FormData) => request<T>(method, path, { form }),
};

export function uploadWithProgress<T>(path: string, form: FormData, onProgress: (pct: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", API_BASE + path);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let data: any = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status === 401) handleUnauthorized();
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, (data && typeof data.detail === "string" && data.detail) || "Error inesperado del servidor."));
    };
    xhr.onerror = () => {
      toast.error(NETWORK_ERROR, { id: "network-error" });
      reject(new ApiError(0, NETWORK_ERROR));
    };
    xhr.send(form);
  });
}

export async function downloadFile(path: string, query: Query, fallbackName: string): Promise<void> {
  const res = await request<Response>("GET", path, { query, raw: true });
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export function fileUrl(u?: string | null): string | null {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND}${u}`;
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Error inesperado.";
}
